import { apiRequest } from "../../Bedrooms/utils/http";
import { buildQuery } from "../utils/adminUtils";

const adminApi = {
  getUsers: () => apiRequest("/api/admin/users"),

  registerUser: (body) =>
    apiRequest("/api/v1/auth/register", {
      method: "POST",
      body,
    }),

  resetPassword: (body) =>
    apiRequest("/api/v1/auth/reset-password", {
      method: "POST",
      body,
    }),

  updateUserEnabled: (id, enabled) =>
    apiRequest(`/api/admin/users/${id}/enabled`, {
      method: "PUT",
      body: { enabled },
    }),

  updateUserRole: (id, role) =>
    apiRequest(`/api/admin/users/${id}/role`, {
      method: "PUT",
      body: { role },
    }),

  // The complete set — [] clears them. 400 on an unknown name.
  updateUserGrants: (id, grants) =>
    apiRequest(`/api/admin/users/${id}/grants`, {
      method: "PUT",
      body: { grants },
    }),

  logoutUser: (userId) =>
    apiRequest(`/api/admin/users/${userId}/logout`, {
      method: "POST",
    }),

  getSessions: () => apiRequest("/api/admin/sessions"),

  getStaff: () => apiRequest("/api/staff"),

  // Every NSR stay, all years: adultId, campus and dates. The supervision
  // screen shows each person's assignments and filters by campus.
  getStaffStays: () => apiRequest("/api/nsrs"),

  // Who supervises whom: a flat list of (supervisor, supervisee) staff links.
  getSupervision: () => apiRequest("/api/admin/supervision"),

  // The complete list for one supervisor — [] clears it. Self-supervision is
  // refused server-side.
  setSupervision: (supervisorStaffId, staffIds) =>
    apiRequest(`/api/admin/supervision/${supervisorStaffId}`, {
      method: "PUT",
      body: { staffIds },
    }),

  getCampuses: () => apiRequest("/api/campuses"),

  getRentCosts: (year) => apiRequest(`/api/rentcosts?year=${year}`),

  // Array body — upserts the whole (year, campus, residentType) card. ADMIN only.
  saveRentCosts: (rows) =>
    apiRequest("/api/rentcosts", {
      method: "PUT",
      body: rows,
    }),

  createCampus: (body) =>
    apiRequest("/api/campuses", {
      method: "POST",
      body,
    }),

  updateCampus: (id, body) =>
    apiRequest(`/api/campuses/${id}`, {
      method: "PUT",
      body,
    }),

  deleteCampus: (id) =>
    apiRequest(`/api/campuses/${id}`, {
      method: "DELETE",
    }),

  bootstrapCalendarWeeks: () =>
    apiRequest(`/api/admin/calendar-weeks/bootstrap`, {
      method: "POST",
    }),

  searchStudents: (term) =>
    apiRequest(`/api/students/search${buildQuery({ term })}`),

  // Full basic student list for a year — same source the main student search
  // filters client-side, so the audit filter can match by name, MT ref OR id.
  getStudentsForYear: (year) =>
    apiRequest(`/api/studentsBasicYear/${year}`),

  getRegisterAudit: (filters = {}) =>
    apiRequest(
      `/api/admin/audit/register${buildQuery({
        email: filters.email,
        startDate: filters.startDate,
        endDate: filters.endDate,
      })}`
    ),

  // HubSpot import: SKU → product-name keyword rules the poller matches on.
  // Row { id, skuCode, campus|null, keyword, productTypes|null, notes, updatedAt }.
  // POST/PUT take the row without id/updatedAt; 409 = duplicate (skuCode, campus).
  getHubspotMappings: () => apiRequest("/api/v1/hubspot/import/mappings"),

  createHubspotMapping: (body) =>
    apiRequest("/api/v1/hubspot/import/mappings", {
      method: "POST",
      body,
    }),

  updateHubspotMapping: (id, body) =>
    apiRequest(`/api/v1/hubspot/import/mappings/${id}`, {
      method: "PUT",
      body,
    }),

  deleteHubspotMapping: (id) =>
    apiRequest(`/api/v1/hubspot/import/mappings/${id}`, {
      method: "DELETE",
    }),

  // Platform-wide settings (key/value, seeded by migration). Everyone can read;
  // only ADMIN can write, and only keys that already exist.
  getSettings: () => apiRequest("/api/settings"),

  updateSetting: (key, value) =>
    apiRequest(`/api/settings/${encodeURIComponent(key)}`, {
      method: "PUT",
      body: { value },
    }),

  // Whether the HubSpot deal import is switched on for THIS backend (a config
  // var, not a setting — only one instance may import). { enabled, dryRun,
  // tokenConfigured, settingKeys } where settingKeys are the hubspot.* rows
  // that are inert while enabled is false.
  getHubspotImportStatus: () => apiRequest("/api/settings/hubspot-import"),

  getAudit: (filters = {}) =>
    apiRequest(
      `/api/admin/audit${buildQuery({
        email: filters.email,
        studentId: filters.studentId,
        startDate: filters.startDate,
        endDate: filters.endDate,
      })}`
    ),
};

export default adminApi;